// Adjustments tab — manual credit / debit / info lines per agent.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { money, apiError, thCls, tdCls, numCls } from "./engineShared";

const TYPE_STYLE = { CREDIT: "bg-emerald-100 text-emerald-800", DEBIT: "bg-rose-100 text-rose-800", ADJUSTMENT: "bg-amber-100 text-amber-800", INFORMATION: "bg-slate-100 text-slate-700" };

export function EngineAdjustments({ canManage, reloadOverview }) {
  const [items, setItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState("all");
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/commissions/engine/adjustments", { params: { agent_id: agentId === "all" ? undefined : agentId } }); setItems(data.items || []); }
    catch (e) { apiError(e, "Could not load adjustments"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [agentId]);
  useEffect(() => { api.get("/commissions/engine/agents").then(({ data }) => setAgents(data.items || [])).catch(() => {}); }, []);

  const remove = async (a) => {
    if (!window.confirm(`Delete this ${a.type.toLowerCase()} of ${money(a.amount)} for ${a.agent}?`)) return;
    try { await api.delete(`/commissions/engine/adjustments/${a.adjustment_id}`); toast.success("Adjustment deleted"); load(); reloadOverview?.(); }
    catch (e) { apiError(e); }
  };

  return (
    <div className="space-y-3" data-testid="engine-adjustments">
      <Card className="border-slate-200 p-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={agentId} onValueChange={setAgentId}>
            <SelectTrigger className="h-9 w-72" data-testid="engine-adj-agent-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72"><SelectItem value="all">All agents</SelectItem>{agents.map((a) => <SelectItem key={a.agent_id} value={a.agent_id}>{a.name}</SelectItem>)}</SelectContent>
          </Select>
          <span className="text-xs text-slate-500" data-testid="engine-adj-count">{items.length} adjustments</span>
          {canManage && <Button className="ml-auto h-9" onClick={() => setEdit({})} data-testid="engine-adj-add"><Plus className="h-4 w-4" /> Add adjustment</Button>}
        </div>
        <p className="mt-2 text-xs text-slate-500">Use a <b>credit</b> for a one-off bonus or a missed commission, a <b>debit</b> for a clawback, an <b>adjustment</b> for corrections and <b>information</b> for a note that carries no money.</p>
      </Card>
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="engine-adj-table">
            <thead className="bg-slate-50"><tr>
              <th className={thCls}>Date</th><th className={thCls}>Agent</th><th className={thCls}>Type</th><th className={thCls}>Description</th>
              <th className={`${thCls} text-right`}>Net</th><th className={`${thCls} text-right`}>Tax</th><th className={`${thCls} text-right`}>Amount</th><th className={thCls}>Period</th><th className={thCls}></th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin" /></td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">No adjustments</td></tr>}
              {!loading && items.map((a) => (
                <tr key={a.adjustment_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`engine-adj-row-${a.adjustment_id}`}>
                  <td className={`${tdCls} whitespace-nowrap`}>{a.date}</td>
                  <td className={`${tdCls} font-medium text-slate-800`}>{a.agent}</td>
                  <td className={tdCls}><Badge className={`${TYPE_STYLE[a.type] || ""} hover:${TYPE_STYLE[a.type] || ""}`}>{a.type}</Badge></td>
                  <td className={tdCls}><div>{a.description}</div>{a.notes && <div className="text-xs text-slate-500">{a.notes}</div>}{a.payment_type && <div className="text-xs text-slate-400">{a.payment_type}</div>}</td>
                  <td className={numCls}>{money(a.net_amount)}</td>
                  <td className={numCls}>{money(a.tax)}</td>
                  <td className={`${numCls} font-medium`}>{money(a.amount)}</td>
                  <td className={`${tdCls} text-slate-600`}>{a.period_label}</td>
                  <td className={`${tdCls} whitespace-nowrap`}>{canManage && (<>
                    <Button variant="ghost" size="sm" onClick={() => setEdit(a)} data-testid={`engine-adj-edit-${a.adjustment_id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => remove(a)} data-testid={`engine-adj-del-${a.adjustment_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </>)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {edit && <AdjustmentDialog adj={edit} agents={agents} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); reloadOverview?.(); }} />}
    </div>
  );
}

function AdjustmentDialog({ adj, agents, onClose, onSaved }) {
  const isNew = !adj.adjustment_id;
  const [f, setF] = useState({ agent_id: adj.agent_id || "", date: adj.date || new Date().toISOString().slice(0, 10), type: adj.type || "CREDIT",
    amount: adj.amount ?? "", tax: adj.tax ?? 0, description: adj.description || "", notes: adj.notes || "", period_label: adj.period_label || "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f, amount: Number(f.amount || 0), tax: Number(f.tax || 0), period_label: f.period_label || null };
      if (isNew) await api.post("/commissions/engine/adjustments", body);
      else await api.put(`/commissions/engine/adjustments/${adj.adjustment_id}`, body);
      toast.success(isNew ? "Adjustment recorded" : "Adjustment saved");
      onSaved();
    } catch (e) { apiError(e, "Could not save"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="engine-adj-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Add adjustment" : "Edit adjustment"}</DialogTitle><DialogDescription>Manual money movement outside the invoice-driven statements.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Agent</Label>
            <Select value={f.agent_id || "none"} onValueChange={(v) => set("agent_id", v === "none" ? "" : v)}>
              <SelectTrigger className="h-9" data-testid="engine-adj-agent"><SelectValue placeholder="Pick the agent" /></SelectTrigger>
              <SelectContent className="max-h-72"><SelectItem value="none">—</SelectItem>{agents.map((a) => <SelectItem key={a.agent_id} value={a.agent_id}>{a.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className="h-9" data-testid="engine-adj-date" /></div>
          <div><Label>Type</Label>
            <Select value={f.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger className="h-9" data-testid="engine-adj-type"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.keys(TYPE_STYLE).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Amount (incl. tax)</Label><Input type="number" step="0.01" value={f.amount} onChange={(e) => set("amount", e.target.value)} className="h-9" data-testid="engine-adj-amount" /></div>
          <div><Label>Tax</Label><Input type="number" step="0.01" value={f.tax} onChange={(e) => set("tax", e.target.value)} className="h-9" data-testid="engine-adj-tax" /></div>
          <div className="col-span-2"><Label>Description</Label><Input value={f.description} onChange={(e) => set("description", e.target.value)} className="h-9" data-testid="engine-adj-description" /></div>
          <div><Label>Period (YYYY-MM, optional)</Label><Input value={f.period_label} onChange={(e) => set("period_label", e.target.value)} placeholder="defaults to the date's month" className="h-9" data-testid="engine-adj-period" /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="engine-adj-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="engine-adj-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !f.agent_id || f.amount === ""} data-testid="engine-adj-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
